import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unifiedApiService } from '../../services/unified';
import { queryKeys } from '../../lib/queryClient';
import type {
  CandidateApplication,
  CandidateWithApplications,
  UpdateCandidateStageRequest,
  CandidateApplicationListResponse,
  MessageResponse,
  PaginationParams,
} from '../../services/unified/types';

/**
 * Query hook for getting all candidate applications for a job
 */
export const useJobCandidates = (jobId: string, params?: PaginationParams) => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'jobs', jobId, 'candidates', params],
    queryFn: (): Promise<CandidateApplicationListResponse> => {
      return unifiedApiService.candidates.getJobCandidates(jobId, params);
    },
    enabled: !!jobId,
    staleTime: 2 * 60 * 1000, // 2 minutes
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Query hook for getting candidate application details
 */
export const useCandidateApplication = (jobId: string, candidateId: string) => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'jobs', jobId, 'candidates', candidateId],
    queryFn: (): Promise<CandidateApplication> => {
      return unifiedApiService.candidates.getCandidateApplication(jobId, candidateId);
    },
    enabled: !!jobId && !!candidateId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Query hook for getting all applications for a specific candidate (across all jobs)
 */
export const useCandidateApplications = (candidateId: string) => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'candidates', candidateId, 'applications'],
    queryFn: (): Promise<CandidateWithApplications> => {
      return unifiedApiService.candidates.getCandidateApplications(candidateId);
    },
    enabled: !!candidateId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Query hook for getting candidates by stage
 */
export const useCandidatesByStage = (stage: string, params?: PaginationParams) => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'candidates', 'stage', stage, params],
    queryFn: (): Promise<CandidateApplicationListResponse> => {
      return unifiedApiService.candidates.getCandidatesByStage(stage, params);
    },
    enabled: !!stage,
    staleTime: 2 * 60 * 1000, // 2 minutes
    placeholderData: (previousData) => previousData,
  });
};

/**
 * Mutation hook for updating candidate stage with optimistic updates
 */
export const useUpdateCandidateStage = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      jobId,
      candidateId,
      data,
    }: {
      jobId: string;
      candidateId: string;
      data: UpdateCandidateStageRequest;
    }): Promise<CandidateApplication> => {
      return unifiedApiService.candidates.updateCandidateStage(jobId, candidateId, data);
    },
    // Optimistic update
    onMutate: async ({ jobId, candidateId, data }) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ 
        queryKey: [...queryKeys.admin.all, 'jobs', jobId, 'candidates', candidateId] 
      });
      
      // Snapshot the previous value
      const previousApplication = queryClient.getQueryData<CandidateApplication>(
        [...queryKeys.admin.all, 'jobs', jobId, 'candidates', candidateId]
      );
      
      // Optimistically update to the new value
      if (previousApplication) {
        queryClient.setQueryData<CandidateApplication>(
          [...queryKeys.admin.all, 'jobs', jobId, 'candidates', candidateId],
          {
            ...previousApplication,
            current_stage: data.stage,
          }
        );
      }
      
      // Return context with the snapshot
      return { previousApplication };
    },
    onSuccess: (updatedApplication, variables) => {
      // Update the specific application in cache with server response
      queryClient.setQueryData(
        [...queryKeys.admin.all, 'jobs', variables.jobId, 'candidates', variables.candidateId],
        updatedApplication
      );
      
      // Invalidate job candidates list to reflect changes
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'jobs', variables.jobId, 'candidates'] 
      });
      
      // Invalidate candidate applications to reflect changes
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'candidates', variables.candidateId, 'applications'] 
      });
      
      // Invalidate candidates by stage queries
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'candidates', 'stage'] 
      });
    },
    // Rollback on error
    onError: (_err, { jobId, candidateId }, context) => {
      if (context?.previousApplication) {
        queryClient.setQueryData(
          [...queryKeys.admin.all, 'jobs', jobId, 'candidates', candidateId],
          context.previousApplication
        );
      }
    },
  });
};

/**
 * Mutation hook for approving candidate for next stage
 */
export const useApproveCandidate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      jobId,
      candidateId,
      notes,
    }: {
      jobId: string;
      candidateId: string;
      notes?: string;
    }): Promise<CandidateApplication> => {
      return unifiedApiService.candidates.approveForNextStage(jobId, candidateId, notes);
    },
    onSuccess: (updatedApplication, variables) => {
      // Update the specific application in cache
      queryClient.setQueryData(
        [...queryKeys.admin.all, 'jobs', variables.jobId, 'candidates', variables.candidateId],
        updatedApplication
      );
      
      // Invalidate all related queries to ensure UI updates
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'jobs', variables.jobId, 'candidates'] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'candidates', variables.candidateId, 'applications'] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'candidates', 'stage'] 
      });
      // Also invalidate the main candidates list used by AdminCandidatesPage
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.admin.candidates() 
      });
      // Invalidate applications queries
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.applications.all 
      });
    },
  });
};

/**
 * Mutation hook for rejecting candidate
 */
export const useRejectCandidate = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({
      jobId,
      candidateId,
      reason,
    }: {
      jobId: string;
      candidateId: string;
      reason: string;
    }): Promise<CandidateApplication> => {
      return unifiedApiService.candidates.rejectCandidate(jobId, candidateId, reason);
    },
    onSuccess: (updatedApplication, variables) => {
      // Update the specific application in cache
      queryClient.setQueryData(
        [...queryKeys.admin.all, 'jobs', variables.jobId, 'candidates', variables.candidateId],
        updatedApplication
      );
      
      // Invalidate all related queries to ensure UI updates
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'jobs', variables.jobId, 'candidates'] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'candidates', variables.candidateId, 'applications'] 
      });
      queryClient.invalidateQueries({ 
        queryKey: [...queryKeys.admin.all, 'candidates', 'stage'] 
      });
      // Also invalidate the main candidates list used by AdminCandidatesPage
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.admin.candidates() 
      });
      // Invalidate applications queries
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.applications.all 
      });
    },
  });
};

/**
 * Mutation hook for sending email to candidate
 */
export const useSendCandidateEmail = () => {
  return useMutation({
    mutationFn: ({
      jobId,
      candidateId,
      emailData,
    }: {
      jobId: string;
      candidateId: string;
      emailData: {
        subject: string;
        content: string;
        cc?: string[];
      };
    }): Promise<MessageResponse> => {
      return unifiedApiService.candidates.sendCandidateEmail(jobId, candidateId, emailData);
    },
    onError: (error) => {
      console.error('Candidate email sending failed:', error);
    },
  });
};
